/**
 * @file CustomersFilterBar.tsx
 * @description Фильтры реестра клиентов (phase19.31): поиск по имени, почте
 * или телефону · «Вид клиента ▾» · «Блокировки ▾» — одной строкой от 1024.
 *
 * Фильтры применяются сразу (`SelectField`), кнопки «Применить» нет; поиск —
 * сам через 0,4 с после ввода и сразу по Enter. До 1024 выпадающие списки
 * уходят в шторку «Фильтры · N».
 */

import { useEffect, useState } from "react";

import { FilterBar } from "../../ui/FilterBar";
import { FilterSheetButton } from "../../ui/FilterSheetButton";
import { SearchInput } from "../../ui/SearchInput";
import { SelectField, type SelectFieldOption } from "../../ui/SelectField";
import { BELOW_LG_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import type { CustomerType } from "../../../lib/api-customers";
import { customersSheetFilterCount, type CustomersRegistryQuery } from "../../../lib/crm-customers-query";

const SEARCH_DELAY_MS = 400;

const TYPE_OPTIONS: SelectFieldOption[] = [
  { value: "", label: "Все клиенты" },
  { value: "INDIVIDUAL", label: "Физлица" },
  { value: "LEGAL", label: "Юрлица" },
];

const BLOCK_OPTIONS: SelectFieldOption[] = [
  { value: "", label: "С блокировкой и без" },
  { value: "1", label: "Только с блокировкой" },
];

interface Props {
  query: CustomersRegistryQuery;
  onChange: (patch: Partial<CustomersRegistryQuery>) => void;
}

export function CustomersFilterBar({ query, onChange }: Props) {
  const compact = useMediaQuery(BELOW_LG_QUERY);
  const [search, setSearch] = useState(query.search);

  // Адрес поменялся извне («Сбросить фильтры», «назад») — поле за ним.
  useEffect(() => setSearch(query.search), [query.search]);

  useEffect(() => {
    if (search.trim() === query.search) return;
    const timer = window.setTimeout(() => onChange({ search: search.trim() }), SEARCH_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [search, query.search, onChange]);

  const selects = (
    <>
      <SelectField
        ariaLabel="Вид клиента"
        value={query.type}
        onChange={(type) => onChange({ type: type as "" | CustomerType })}
        options={TYPE_OPTIONS}
        className={compact ? "w-full" : "w-[180px]"}
      />
      <SelectField
        ariaLabel="Блокировки"
        value={query.blockedOnly ? "1" : ""}
        onChange={(value) => onChange({ blockedOnly: value === "1" })}
        options={BLOCK_OPTIONS}
        className={compact ? "w-full" : "w-[220px]"}
      />
    </>
  );

  return (
    <FilterBar
      search={
        <form
          role="search"
          onSubmit={(event) => {
            event.preventDefault();
            onChange({ search: search.trim() });
          }}
        >
          <SearchInput value={search} onChange={setSearch} placeholder="Имя, почта или телефон" ariaLabel="Поиск клиента" />
        </form>
      }
    >
      {compact ? (
        <FilterSheetButton count={customersSheetFilterCount(query)} onReset={() => onChange({ type: "", blockedOnly: false })}>
          {selects}
        </FilterSheetButton>
      ) : (
        selects
      )}
    </FilterBar>
  );
}
